import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { WIDGET_SIZES } from './sizes.js';
import { WIDGET_CATEGORIES } from './widgetRegistry.js';
import { galleryCategories, galleryEntries } from './gallery.js';
import { WidgetPreview, previewBox } from './WidgetPreview.jsx';

const SHEET_WIDTH = 440;
const PREVIEW_MAX_WIDTH = SHEET_WIDTH - 48;

/** One gallery row: title, description, size picker, live preview of the picked size, Add. */
function GalleryEntry({ widget, placed, onAdd }) {
  const [size, setSize] = useState(widget.defaultSize);
  const scale = Math.min(0.5, PREVIEW_MAX_WIDTH / previewBox(size).width);
  return (
    <Box component="li" sx={{ listStyle: 'none', py: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {widget.title}
          </Typography>
          {widget.description && (
            <Typography variant="body2" color="text.secondary">
              {widget.description}
            </Typography>
          )}
        </Box>
        {placed > 0 && <Chip size="small" icon={<CheckIcon />} color="success" variant="outlined" label={placed > 1 ? `Added ×${placed}` : 'Added'} />}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 1 }} useFlexGap flexWrap="wrap">
        <ToggleButtonGroup size="small" exclusive value={size} onChange={(_, v) => v && setSize(v)} aria-label={`${widget.title} size`}>
          {widget.sizes.map((k) => (
            <ToggleButton key={k} value={k} aria-label={WIDGET_SIZES[k].label}>
              {WIDGET_SIZES[k].label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button variant={placed ? 'outlined' : 'contained'} size="small" startIcon={<AddIcon />} onClick={() => onAdd(widget, size)}>
          {placed ? 'Add another' : 'Add'}
        </Button>
      </Stack>
      <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5, display: 'flex', justifyContent: 'center' }}>
        <WidgetPreview key={size} widget={widget} size={size} scale={scale} />
      </Box>
    </Box>
  );
}

/**
 * The "+" widget gallery (Beta-1 plan P3-17): a right-hand sheet with search, category chips,
 * live previews in the sizes each widget supports, Add (places the widget in the first free slot —
 * the grid scrolls to it and highlights it) and "Added" on widgets already on the layout. Lists only
 * widgets the viewer's access level allows (registry availability; P3-21 matrix).
 *
 * Props
 *   open, onClose
 *   editor        useDashboardEditor() value (uses editor.layout and editor.add)
 *   registry      WidgetRegistry
 *   role, dashboard, widgetContext   availability context, same as WidgetGrid
 *   onAdded(item) optional callback after a widget is placed
 */
export function WidgetGallery({ open, onClose, editor, registry, role = null, dashboard = null, widgetContext = null, onAdded }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(null);
  const ctx = useMemo(() => ({ role, dashboard, ...(widgetContext || {}) }), [role, dashboard, widgetContext]);
  const categories = useMemo(() => galleryCategories(registry, ctx), [registry, ctx]);
  const entries = useMemo(
    () => galleryEntries(registry, { ...ctx, layout: editor.layout, query, category }),
    [registry, ctx, editor.layout, query, category]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setCategory(null);
    }
  }, [open]);

  const handleAdd = (widget, size) => {
    editor.add(widget, { size });
    if (onAdded) onAdded(widget, size);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (t) => t.zIndex.drawer + 2 }} PaperProps={{ sx: { width: { xs: '100%', sm: SHEET_WIDTH } } }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            Add a widget
          </Typography>
          <Tooltip title="Close">
            <IconButton aria-label="Close widget gallery" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder="Search widgets"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ 'aria-label': 'Search widgets' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mt: 1.5 }}
        />
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }} role="group" aria-label="Categories">
          <Chip label="All" size="small" color={category ? 'default' : 'primary'} onClick={() => setCategory(null)} />
          {categories.map((c) => (
            <Chip key={c.key} label={c.label} size="small" color={category === c.key ? 'primary' : 'default'} onClick={() => setCategory(c.key)} />
          ))}
        </Stack>
      </Box>
      <Divider />
      <Box component="ul" sx={{ m: 0, px: 2, py: 0, overflowY: 'auto', flex: 1 }}>
        {entries.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No widgets match{query ? ` “${query}”` : ''}{category ? ` in ${WIDGET_CATEGORIES[category]}` : ''}.
          </Typography>
        )}
        {entries.map(({ widget, placed }, idx) => (
          <React.Fragment key={widget.id}>
            {idx > 0 && <Divider component="li" sx={{ listStyle: 'none' }} />}
            <GalleryEntry widget={widget} placed={placed} onAdd={handleAdd} />
          </React.Fragment>
        ))}
      </Box>
    </Drawer>
  );
}

export default WidgetGallery;
